// MemberAvatar - Avatar component that fetches proper profile photo URLs
// Matches mobile MemberAvatar pattern using useProfilePhotoUrl hook

import React from 'react';
import { Avatar, AvatarProps } from '@mui/material';
import { useProfilePhotoUrl } from '../../hooks/useProfilePhotoUrl';

interface MemberAvatarProps extends Omit<AvatarProps, 'src'> {
  /** The profile photo file ID to fetch URL for */
  profilePhotoId?: string | null;
  /** Member's name for generating initials fallback */
  name?: string;
  /** Background color for the avatar when showing initials */
  color?: string;
}

/**
 * Avatar component that fetches profile photo thumbnail URLs.
 * Uses useProfilePhotoUrl hook to get presigned URLs from the media service.
 * Falls back to initials with background color if no photo available.
 */
export const MemberAvatar: React.FC<MemberAvatarProps> = ({
  profilePhotoId,
  name = '',
  color,
  children,
  sx,
  ...avatarProps
}) => {
  // Fetch thumbnail URL using the same hook as mobile
  const photoUrl = useProfilePhotoUrl(profilePhotoId, true);

  // Generate initials from name
  const initials = name
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  return (
    <Avatar
      src={photoUrl || undefined}
      sx={{
        bgcolor: color || '#96D0F0',
        ...sx,
      }}
      {...avatarProps}
    >
      {children || initials}
    </Avatar>
  );
};

export default MemberAvatar;
